import {ErrorHandler, Injectable} from '@angular/core';
import {
  Contact,
  Customer,
  FileSubresourcePath,
  InsuranceCategoryFamily,
  InsuranceService,
  Ref,
  Ticket,
  TicketService,
  TicketStatus,
  TicketType,
} from '@lifeislife/lifeislife-domain';
import {filter, map, publishReplay, refCount, switchMap, tap} from 'rxjs/operators';
import {BehaviorSubject, Observable, Subject, throwError} from 'rxjs';
import {CustomerContextService} from './customer-context-service';
import {PendingTicketDraftService} from './pending-ticket-draft.service';
import {ShellMessageService} from './shell-message-service';
import {isSameRef} from './utils';

@Injectable({
  providedIn: 'root',
})
export class CustomerInsuranceInformationRequestHelperService {

  editingTicketDraft$ = new BehaviorSubject<Ticket>(null);

  committingDraft$ = new BehaviorSubject<Ref<Ticket> | null>(null);
  cancellingDraft$ = new BehaviorSubject<Ref<Ticket> | null>(null);
  updatingDraft$ = new BehaviorSubject<Ref<Ticket> | null>(null);

  committedDrafts$ = new Subject<Ref<Ticket>>();

  constructor(
    private customerContextService: CustomerContextService,
    private ticketService: TicketService,
    private insuranceService: InsuranceService,
    private pendingTicketDraftService: PendingTicketDraftService,
    private shellMessageService: ShellMessageService,
    private errorHandler: ErrorHandler,
  ) {
  }

  createDraft$(insuranceFamily: InsuranceCategoryFamily | null): Observable<Ticket> {
    // Create draft right away. Update when response lands.
    const contactRef = this.customerContextService.getActiveContactRef();
    const customerRef = this.customerContextService.getActiveCustomerRef();

    const subject = this.getNewTicketSubject(insuranceFamily);
    const tmpDraft = {
      subject: subject,
    } as Ticket;
    this.editingTicketDraft$.next(tmpDraft);
    return this.createNewTicketDraft$(contactRef, insuranceFamily, tmpDraft).pipe(
      switchMap(ticketRef => this.setTicketCustomer$(ticketRef, customerRef)),
      switchMap(ticketRef => this.ticketService.getTicket$(ticketRef)),
      tap(draft => {
          draft.subject = tmpDraft.subject;
          draft.description = tmpDraft.description;
          this.editingTicketDraft$.next(draft);
        },
        e => {
          this.shellMessageService.addError(`Une erreur est survenue lors de la création de la demande`, e);
          this.pendingTicketDraftService.reload();
        }),
    );
  }

  loadDraft(ticketRef: Ref<Ticket>) {
    this.ticketService.getTicket$(ticketRef)
      .subscribe(ticket => this.editingTicketDraft$.next(ticket));
  }

  updateDraft(update: Partial<Ticket>) {
    const currentDraft = this.editingTicketDraft$.getValue();
    if (currentDraft == null) {
      return throwError(new Error(`No active draft`));
    }

    const updatedDraft = Object.assign({}, currentDraft, update);
    this.updatingDraft$.next({id: updatedDraft.id});
    this.ticketService.saveTicket$(updatedDraft).pipe(
      switchMap(ref => this.ticketService.getTicket$(ref)),
    ).subscribe(d => this.editingTicketDraft$.next(d),
      e => {
        // No message: action not triggered by user action
        this.errorHandler.handleError(e);
      },
      () => {
        this.pendingTicketDraftService.reload();
        this.updatingDraft$.next(null);
      });
  }

  getDraftUploadFilePath$(): Observable<FileSubresourcePath<Ticket> | null> {
    return this.editingTicketDraft$.pipe(
      map(d => d == null ? null : this.ticketService.getNewTicketFilePath(d)),
      publishReplay(1), refCount(),
    );
  }

  commitDraft(ticket?: Ticket) {
    const draft = ticket == null ? this.editingTicketDraft$.getValue() : ticket;

    this.committingDraft$.next({id: draft.id});
    return this.ticketService.saveTicket$(draft).pipe(
      switchMap(ref => this.ticketService.updateTicketStatus$(ref, TicketStatus.CREATED)),
      switchMap(ref => this.ticketService.getTicket$(ref)),
    ).subscribe(
      (t) => {
        this.committedDrafts$.next({id: t.id});
        if (isSameRef(this.editingTicketDraft$.getValue(), draft)) {
          this.editingTicketDraft$.next(null);
        }
        this.shellMessageService.addInfo(`Votre demande a bien été envoyée`,
          'Nous reprendrons contact avec vous dans les plus brefs délais');
      },
      e => this.shellMessageService.addError(`Impossible d'envoyer la demande`, e),
      () => {
        this.pendingTicketDraftService.reload();
        this.committingDraft$.next(null);
      },
    );
  }

  cancelDraft(ticketRef?: Ref<Ticket>, showError?: boolean) {
    const draftRef = ticketRef == null ? this.editingTicketDraft$.getValue() : ticketRef;
    if (isSameRef(this.cancellingDraft$.getValue(), draftRef)) {
      return;
    }
    const removingCurrentDraft = isSameRef(this.editingTicketDraft$.getValue(), draftRef);
    if (draftRef && draftRef.id) {
      this.cancellingDraft$.next(draftRef);
      return this.ticketService.deleteTicket$(draftRef.id)
        .subscribe(() => {
            if (removingCurrentDraft) {
              this.editingTicketDraft$.next(null);
            }
          },
          e => {
            if (showError) {
              this.shellMessageService.addError(`Impossible de supprimer la demande`, e);
            }
            this.errorHandler.handleError(e);
          },
          () => {
            this.pendingTicketDraftService.reload();
            this.cancellingDraft$.next(null);
          },
        );
    }
  }

  getActiveDraft$() {
    return this.editingTicketDraft$;
  }

  isCommitting$(ref: Ref<Ticket>) {
    return this.committingDraft$.pipe(
      map(r => isSameRef(r, ref)),
      publishReplay(1), refCount(),
    );
  }

  isUpdating$(ref: Ref<Ticket>) {
    return this.updatingDraft$.pipe(
      map(r => isSameRef(r, ref)),
      publishReplay(1), refCount(),
    );
  }

  isCancelling$(ref: Ref<Ticket>) {
    return this.cancellingDraft$.pipe(
      map(r => isSameRef(r, ref)),
      publishReplay(1), refCount(),
    );
  }

  getCommitted$(ref: Ref<Ticket>) {
    return this.committedDrafts$.pipe(
      filter(r => isSameRef(r, ref)),
      publishReplay(1), refCount(),
    );
  }

  private createNewTicketDraft$(contactRef: Ref<Contact>, insuranceFamily: InsuranceCategoryFamily | null, baseTicket: Ticket): Observable<Ref<Ticket>> {
    if (contactRef == null) {
      return throwError(new Error(`Aucun contact loggé`));
    }
    const newTicket: Ticket = Object.assign({}, baseTicket, {
      ticketType: TicketType.CUSTOMER_INSURANCE_REQUEST,
      ticketStatus: TicketStatus.DRAFT,
      authorContactRef: contactRef,
    } as Ticket);
    return this.ticketService.saveTicket$(newTicket);
  }


  private setTicketCustomer$(ticketRef: Ref<Ticket>, customerRef: Ref<Customer>): Observable<Ref<Ticket>> {
    return this.ticketService.addTicketCustomer$(ticketRef, customerRef).pipe(
      map(r => ticketRef),
    );
  }

  private getNewTicketSubject(insuranceFamily: InsuranceCategoryFamily) {
    if (insuranceFamily == null) {
      return `Demande d'information`; // TODO i18n
    }
    const familyLabel = this.insuranceService.getInsuranceFamilyLabel(insuranceFamily);
    return `Demande d'information pour les produits ${familyLabel}`;
  }

}
